// Pure SEO policy shared by loaders and the browser. Do not import server-only modules here.

// Account, cart and internal search are utility pages: no unique content, and
// /search is an unbounded URL space. They used to be served `index,follow`
// while robots.txt blocked them, which is the worst of both worlds — Google
// could not fetch them to learn they were unwanted, so it indexed the bare
// URLs ("Indexed, though blocked by robots.txt"). They are crawlable now and
// carry noindex instead. `follow` keeps link discovery intact.
export const NOINDEX_PATH =
  /^\/(?:(?:en|zh-cn)\/)?(?:account|cart|search|api|featured-products|discount)(?:\/|$)/i;

// '/zh-cn' renders byte-identical Traditional Chinese from the server — the
// Simplified conversion is a client-side effect (SimplifiedChinese.tsx), and
// the document even ships `lang="ZH_TW"`. It is therefore a second copy of
// every page on the site: same title, same headings, same body, its own
// self-referencing canonical, `index,follow`. sitemap.server.ts already
// refuses to list it for exactly that reason, but the pages still invited
// Google in — every page linked to its /zh-cn twin via an hreflang alternate.
// Serve the locale to shoppers who pick it; keep it out of the index.
//
// Both rules here are case-insensitive: getLocaleFromRequest lowercases the
// first path segment (app/lib/utils.ts), so /ZH-CN/products/x resolves to the
// zh-cn locale and serves the duplicate. A case-sensitive rule would have
// left that spelling indexable.
export const DUPLICATE_LOCALE_PATH = /^\/zh-cn(?:\/|$)/i;

/** Pages that must not be indexed: utility pages and the /zh-cn duplicate. */
export function isNoIndexPath(pathname: string): boolean {
  return (
    NOINDEX_PATH.test(pathname) ||
    DUPLICATE_LOCALE_PATH.test(pathname) ||
    /^\/(?:(?:en|zh-cn)\/)?\d+\/orders\/[^/]+\/authenticate(?:\/|$)/i.test(
      pathname,
    )
  );
}

/** Keep preview deployments and unbounded collection refinements out of search. */
export function isNoIndexUrl(url: string): boolean {
  try {
    const {hostname, pathname, searchParams} = new URL(url);
    if (
      hostname !== 'helmetking.com' &&
      !hostname.endsWith('.helmetking.com')
    ) {
      return true;
    }
    if (isNoIndexPath(pathname)) return true;
    if (
      !/^\/(?:(?:en|zh-cn)\/)?(?:collections\/[^/]+|products)\/?$/i.test(
        pathname,
      )
    ) {
      return false;
    }
    const isVendorPage = /\/collections\/vendor\/?$/i.test(pathname);
    return Array.from(searchParams.keys()).some(
      (key) =>
        key === 'sort' ||
        key === 'sort_by' ||
        key.startsWith('filter.') ||
        (key.startsWith('pf_') && !(isVendorPage && key === 'pf_v_brand')),
    );
  } catch {
    return true;
  }
}

// A title that already ends in '| 頭盔王', '| Helmetking' or
// '| Helmetking 頭盔王'.
const TITLE_BRAND_SUFFIX = /\|\s*(?:頭盔王|helmet\s?king(?:\s*頭盔王)?)\s*$/i;
const TITLE_BRAND_ONLY = /^(?:頭盔王|helmet\s?king(?:\s*頭盔王)?)$/i;

/** Keep CMS titles intact and append the site name only once. */
export function titleTemplateFor(title?: string | null): string {
  const normalized = title?.trim() || '';
  return TITLE_BRAND_SUFFIX.test(normalized) ||
    TITLE_BRAND_ONLY.test(normalized)
    ? '%s'
    : '%s | Helmetking 頭盔王';
}

/**
 * Canonical form of a request URL for `<link rel="canonical">`: same
 * host + pathname, with the query string and hash stripped — except for the
 * params named in `keepParams`. This collapses faceted-navigation, sort and
 * tracking permutations (?filter.*, ?sort, ?utm_*, ?fbclid, …) onto one
 * canonical URL so Google doesn't index near-duplicates or waste crawl budget
 * on them. Locale path prefixes (/en, /zh-cn) are preserved — those are
 * genuinely distinct pages.
 *
 * `keepParams` exists because some pages carry their identity in the query
 * string. A paginated PLP is the main case: ?page=2 lists products that appear
 * on no other URL, and canonicalising it to page 1 tells Google not to keep
 * them. Passing nothing keeps every existing caller byte-identical.
 */
/**
 * The one host the storefront is allowed to claim as canonical.
 *
 * The same application answers on several names — hk.helmetking.com and
 * en.helmetking.com serve a byte-identical copy of the site, and the bare
 * apex resolves too. Every one of them used to emit a canonical pointing at
 * itself, so each product existed as several perfect duplicates, each
 * insisting it was the original. That is what Search Console reports as
 * "Duplicate without user-selected canonical".
 */
export const CANONICAL_ORIGIN = 'https://www.helmetking.com';

/** The origin a page should present as its own, folded onto the canonical host. */
export function canonicalOrigin(url: string): string {
  try {
    return new URL(canonicalUrl(url)).origin;
  } catch {
    return CANONICAL_ORIGIN;
  }
}

export function canonicalUrl(url: string, keepParams: string[] = []): string {
  try {
    const u = new URL(url);
    // Only *.helmetking.com is folded onto the canonical host. localhost and
    // *.vercel.app keep their own origin so dev and previews stay honest
    // about which deployment is being looked at.
    // tw.helmetking.com is excluded: the code still models it as a separate
    // market (its own currency and locale), so it must never claim the HK
    // site's URLs even while its DNS redirects away.
    if (
      u.hostname !== 'tw.helmetking.com' &&
      (u.hostname === 'helmetking.com' ||
        u.hostname.endsWith('.helmetking.com'))
    ) {
      const canonicalHost = new URL(CANONICAL_ORIGIN);
      u.protocol = canonicalHost.protocol;
      u.host = canonicalHost.host;
    }
    const kept = new URLSearchParams();
    for (const key of keepParams) {
      for (const value of u.searchParams.getAll(key)) {
        // '1' is the implicit default for page — keep it off the canonical.
        if (value && !(key === 'page' && value === '1'))
          kept.append(key, value);
      }
    }
    const qs = kept.toString();
    u.search = qs ? `?${qs}` : '';
    u.hash = '';
    // Remix serves '/en' and '/en/' alike, and each was self-canonicalising,
    // so one page held two indexable URLs. Fold the trailing slash away —
    // the site root keeps its own '/', which is the only form it has.
    if (u.pathname.length > 1 && u.pathname.endsWith('/')) {
      u.pathname = u.pathname.replace(/\/+$/, '');
    }
    // The locale prefix is matched case-insensitively when the request is
    // routed, so /EN/products/x serves the English page and used to declare
    // itself canonical — one more spelling of a page that already has one.
    // Fold it to the lowercase form the sitemaps and internal links use.
    u.pathname = u.pathname.replace(/^\/(en|zh-cn)(?=\/|$)/i, (m) =>
      m.toLowerCase(),
    );
    // Percent-escapes carry through from the request untouched, so a handle
    // with CJK in it — and 58 of ours have — canonicalised to whichever case
    // the requester happened to use: %E6… for one visitor, %e6… for the next,
    // two self-canonical spellings of one page. RFC 3986 §6.2.2.1 makes
    // uppercase the normalised form, and it is what `new URL()` emits when it
    // encodes a path, so the sitemap can match it byte for byte.
    u.pathname = u.pathname.replace(/%[0-9a-fA-F]{2}/g, (esc) =>
      esc.toUpperCase(),
    );
    return u.toString();
  } catch {
    return url;
  }
}
